import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../common/LanguageSelector";
import TenantSelector from "../../common/TenantSelector";
import DataAreaSelector from "../../common/DataAreaSelector";
import DynamicLogo from "../../common/DynamicLogo";
import MessagingService from "../../../services/core/MessagingService";
import { useAuth } from "../../../contexts/AuthContext";

const Header = ({ data }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission, isAuthenticated } = useAuth();
  const [username, setDisplayName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [messageCounts, setMessageCounts] = useState({ totalUnread: 0, criticalUnread: 0 });

  // Estado para controlar la visibilidad del sidebar
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Estado para controlar el dropdown del usuario
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    document.documentElement.classList.toggle("sidebar-left-opened");
  };

  useEffect(() => {
    const storedUsername = localStorage.getItem("displayName");
    if (storedUsername) {
      setDisplayName(storedUsername);
    }

    // Obtener el rol del usuario desde userData
    const userDataStr = localStorage.getItem("userData");
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        const roles = userData.roles;
        if (Array.isArray(roles) && roles.length > 0) {
          setUserRole(roles[0]);
        } else if (typeof roles === "string") {
          setUserRole(roles);
        }
      } catch (error) {
        console.error("Error parsing userData:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !hasPermission("messaging.read")) {
      return undefined;
    }

    MessagingService.counts()
      .then(setMessageCounts)
      .catch(() => {});

    return MessagingService.subscribe((payload, eventName) => {
      if (eventName === "message-counts" && payload) {
        setMessageCounts(payload);
      }
    });
  }, [hasPermission, isAuthenticated]);

  const handleLogout = () => {
    // Limpiar ambos storages
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("refreshToken");
    
    localStorage.removeItem("userData");
    localStorage.removeItem("displayName");
    localStorage.removeItem("selectedTenant");
    localStorage.removeItem("selectedTenantId");
    localStorage.removeItem("selectedTenantCode");
    localStorage.removeItem("tenantId");
    localStorage.removeItem("rememberMe");
    localStorage.removeItem("dataAreaId");
    
    navigate("/");
  };

  return (
    <>
      <header className="header">
        <div className="logo-container">
          <div
            className="visible-xs toggle-sidebar-left"
            onClick={toggleSidebar}
          >
            <i className="fa fa-bars" aria-label="Toggle sidebar"></i>
          </div>
          <div className="logo">
            <div className="font-logo custom-primary-color">
              <DynamicLogo alt="Logo" className="logo-img" />
            </div>
          </div>
        </div>

        <div className="header-right">
          <TenantSelector />
          <DataAreaSelector />
          <LanguageSelector />

          {hasPermission("messaging.read") && (
            <button
              type="button"
              className={`header-message-button ${messageCounts.criticalUnread > 0 ? "critical" : ""}`}
              onClick={() => navigate("/messages")}
              title={t("header.messages")}
            >
              <i className="fa fa-envelope"></i>
              {messageCounts.totalUnread > 0 && (
                <span className="header-message-badge">
                  {messageCounts.totalUnread > 99 ? "99+" : messageCounts.totalUnread}
                </span>
              )}
            </button>
          )}

          <div className="modern-userbox">
            <button
              type="button"
              className="userbox-toggle"
              onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
            >
              <div className="user-avatar">
                <i className="fa fa-user"></i>
              </div>
              <div className="user-info">
                <span className="user-name">{username}</span>
                <span className="user-role">{userRole || ""}</span>
              </div>
              <i
                className={`fa fa-chevron-down user-chevron ${isUserDropdownOpen ? "open" : ""}`}
              ></i>
            </button>

            {isUserDropdownOpen && (
              <>
                <div
                  className="dropdown-overlay"
                  onClick={() => setIsUserDropdownOpen(false)}
                ></div>
                <div className="modern-dropdown-menu">
                  <div className="dropdown-header">
                    <div className="dropdown-user-info">
                      <div className="dropdown-avatar">
                        <i className="fa fa-user"></i>
                      </div>
                      <div>
                        <div className="dropdown-username">{username}</div>
                        <div className="dropdown-userrole">
                          {userRole || ""}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-body">
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => {
                        navigate("/profile");
                        setIsUserDropdownOpen(false);
                      }}
                    >
                      <i className="fa fa-id-card dropdown-icon"></i>
                      <span>{t("header.profile")}</span>
                    </button>
                    <button
                      type="button"
                      className="dropdown-item logout-item"
                      onClick={(e) => {
                        e.preventDefault();
                        handleLogout();
                      }}
                    >
                      <i className="fa fa-power-off dropdown-icon"></i>
                      <span>{t("header.logout")}</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
